import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";
import { fetchSearchCount } from "../../src/utils/fetchSearchCount";

// Connects to data-controller="job-filter--sp-work-place-modal"
export default class extends Controller {
  static targets = ["modal", "checkbox", "regionCheckbox", "form", "searchCount", "searchCountUnit", "submitButton"];

  declare modalTarget: HTMLElement;
  declare checkboxTargets: HTMLInputElement[];
  declare regionCheckboxTargets: HTMLInputElement[];
  declare formTarget: HTMLFormElement;
  declare searchCountTarget: HTMLElement;
  declare searchCountUnitTarget: HTMLElement;
  declare submitButtonTarget: HTMLButtonElement;

  modal: Modal;
  debounceTimeout = 0; // デバウンス管理用

  connect() {
    this.modal = new Modal(this.modalTarget);

    // 地域チェックボックスが存在する場合のみ、イベントリスナーを追加
    this.setupRegionCheckboxListeners();
  }

  openModal(_event: CustomEvent): void {
    // 県のチェック状態に基づいて地域チェックボックスを更新
    this.updateRegionCheckboxes();

    this.fetchModalSearchCountWithDebounce();

    this.modal.show();
  }

  closeModal(): void {
    this.modal.hide();
  }

  // change イベント
  handleWorkPlaceChange(_event: Event): void {
    this.fetchModalSearchCountWithDebounce();
  }

  // 地域チェックボックスのイベントリスナーをセットアップ
  setupRegionCheckboxListeners(): void {
    this.regionCheckboxTargets.forEach((regionCheckbox) => {
      regionCheckbox.addEventListener("change", (event) => {
        const target = event.target as HTMLInputElement;
        const regionId = target.value;
        const isChecked = target.checked;

        // 地域に属する県のチェックボックスを取得
        const prefectureCheckboxes = this.checkboxTargets.filter((checkbox) => checkbox.dataset.regionId === regionId);

        // 県のチェックボックスの状態を更新
        prefectureCheckboxes.forEach((checkbox) => {
          checkbox.checked = isChecked;
        });
      });
    });

    // 県のチェックボックスにイベントリスナーを追加
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.updateRegionCheckboxes();
      });
    });
  }

  // 各地域チェックボックスの状態を、所属する県のチェック状態に基づいて更新
  updateRegionCheckboxes(): void {
    // Stimulusのターゲットを使用して地域チェックボックスを処理
    this.regionCheckboxTargets.forEach((regionCheckbox) => {
      const regionId = regionCheckbox.value;

      // この地域に属する全ての県チェックボックスを取得
      const prefectureCheckboxes = this.checkboxTargets.filter((checkbox) => checkbox.dataset.regionId === regionId);

      if (prefectureCheckboxes.length === 0) return;

      // 全ての県がチェックされているかどうか確認
      const allChecked = prefectureCheckboxes.every((checkbox) => checkbox.checked);

      // 全ての県がチェックされている場合は地域もチェック, そうでない場合は地域のチェックを外す
      regionCheckbox.checked = allChecked;
    });
  }

  // リセットボタンの処理
  reset(): void {
    // すべてのチェックボックスの選択を解除
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // 地域のチェックボックスも選択解除
    this.regionCheckboxTargets.forEach((regionCheckbox) => {
      regionCheckbox.checked = false;
    });

    this.fetchModalSearchCountWithDebounce();
  }

  // フォーム変更時の処理
  private fetchModalSearchCountWithDebounce() {
    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = window.setTimeout(async () => {
      const count = await fetchSearchCount({ form: this.formTarget });
      this.updateModalSearchCount(Number(count));
      this.submitButtonTarget.disabled = Number(count) <= 0;
    }, 500);
  }

  // モーダル内の検索件数表示を更新する
  private updateModalSearchCount(count: number): void {
    if (count > 999) {
      this.searchCountTarget.textContent = "999";
      this.searchCountUnitTarget.textContent = "件以上";
    } else {
      this.searchCountTarget.textContent = count.toString();
      this.searchCountUnitTarget.textContent = "件";
    }
  }
}
